// src/portal-app/shell/kit/FilterSheet.js
//
// Filters live in a sheet, never stacked on the surface.
//
// The Transactions screen previously showed a segmented control, a date-chip
// row, a summary bar, and THREE categorical chip rows (staff, payment,
// dining) plus a search field before a single transaction appeared — seven
// control rows. This button plus sheet replaces the three chip rows, and the
// same fix applies to Products, Inventory, Expenses, Reservations and
// Schedule (spec §5 P4).
import React, { useEffect, useRef } from 'react';
import { GlassCard } from '../LiquidGlass';
import { useLanguage } from '../../../context/LanguageContext';
import { ensureKitCss } from './kitCss';
import { IconFilter, IconClose } from './icons';

// This is the kit's first modal, so whatever this queries becomes the
// pattern every later sheet copies. Matches spec §9's focus-order rules;
// [tabindex]:not([tabindex="-1"]) excludes elements deliberately pulled out
// of tab order rather than treating them as focusable.
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function FilterButton({ activeCount = 0, onPress }) {
    ensureKitCss();
    const { t } = useLanguage();
    const label = t('filters', {}, 'Filters');
    const aria = activeCount > 0
        ? `${label}, ${activeCount} ${t('active_lower', {}, 'active')}`
        : label;

    return (
        <button
            type="button"
            className="pk-fb"
            onClick={onPress}
            aria-label={aria}
            data-testid="filter-button"
        >
            <IconFilter />
            <span>{label}</span>
            {activeCount > 0 && <span className="pk-fb-badge pk-num">{activeCount}</span>}
        </button>
    );
}

export function FilterSheet({ open, onClose, groups = [], value = {}, onChange, onClear }) {
    ensureKitCss();
    const { t } = useLanguage();
    // Ref sits on the backdrop, not GlassCard — GlassCard is a plain function
    // component (no forwardRef), and the backdrop has no other focusable
    // descendants, so it scopes the focus trap identically to the sheet.
    const containerRef = useRef(null);
    const closeRef = useRef(null);
    const previousFocusRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onClose && onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    // Focus management + scroll lock, kept in its own effect (still keyed
    // only on `open`) so the Escape handler above stays a single concern.
    // Every listener and style mutation here is undone in the cleanup —
    // this runs on every screen that opens a sheet, so a leak compounds.
    useEffect(() => {
        if (!open) return undefined;

        previousFocusRef.current = document.activeElement;
        if (closeRef.current) closeRef.current.focus();

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Focusable children are queried at keydown time, not captured once
        // at mount: the chip set changes as filters are picked/cleared, so a
        // list snapshotted on open would go stale mid-session.
        const trapFocus = (e) => {
            if (e.key !== 'Tab' || !containerRef.current) return;
            const focusable = Array.from(
                containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', trapFocus);

        return () => {
            document.removeEventListener('keydown', trapFocus);
            document.body.style.overflow = prevOverflow;
            const toRestore = previousFocusRef.current;
            if (toRestore && typeof toRestore.focus === 'function') {
                toRestore.focus();
            }
        };
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="pk-fs-backdrop"
            role="presentation"
            ref={containerRef}
            onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
        >
            <GlassCard
                veil
                className="pk-fs-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={t('filters', {}, 'Filters')}
                data-testid="filter-sheet"
            >
                <div className="pk-fs-head">
                    <span className="pk-fs-title">{t('filters', {}, 'Filters')}</span>
                    <button
                        type="button"
                        className="pk-fs-clear"
                        onClick={onClear}
                    >
                        {t('clear_all', {}, 'Clear all')}
                    </button>
                    <button
                        type="button"
                        className="pk-fs-close"
                        ref={closeRef}
                        onClick={onClose}
                        aria-label={t('close', {}, 'Close')}
                    >
                        <IconClose />
                    </button>
                </div>

                {groups.map((g) => {
                    const selected = value[g.id] != null ? value[g.id] : null;
                    return (
                        <div className="pk-fs-group" key={g.id}>
                            <div className="pk-label">{g.label}</div>
                            <div className="pk-fs-chips">
                                <button
                                    type="button"
                                    className={`pk-fs-chip${selected === null ? ' pk-fs-chip-active' : ''}`}
                                    onClick={() => onChange && onChange(g.id, null)}
                                >
                                    {t('all', {}, 'All')}
                                </button>
                                {g.options.map((o) => (
                                    <button
                                        key={o.id}
                                        type="button"
                                        className={`pk-fs-chip${selected === o.id ? ' pk-fs-chip-active' : ''}`}
                                        onClick={() => onChange && onChange(g.id, o.id)}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </GlassCard>
        </div>
    );
}

export default FilterSheet;
